import { isObject } from './isObject';

type BreakpointVariable = {
  key: string
  name: string | null
  fallback?: number
  replacement?: Replacement 
}

type CaptureConfig = {
  element: HTMLElement | string
  pseudo?: PseudoElements
  variables: BreakpointVariable[]
}

type MqConfig = {
  [key: string]: number
}

type PseudoElements = '::after' | '::before' | '::marker' | '::line-marker' | null

type Replacement = {
  match: string
  replace: string
}

type SortOrder = 'asc' | 'desc'

const getPropertiesFromElement = (element: HTMLElement | string, pseudo: PseudoElements = null, variables: BreakpointVariable[]) => {
  const target = (element instanceof HTMLElement)
      ? element
      : document.querySelector(element);

  const styles = getComputedStyle(target as HTMLElement, pseudo);

  return variables.reduce((acc: MqConfig, { key, name, fallback, replacement }: BreakpointVariable) => {
    const { match, replace } = (isObject(replacement)) 
      ? replacement as Replacement
      : { match: 'px', replace: '' } as Replacement
    
    const val = name && styles.getPropertyValue(name);
    return {
      ...acc,
      [key]: val ? val.replace(match, replace) : fallback,
    } as MqConfig
  }, {} as MqConfig);
}

const getCapture = ({ element, pseudo, variables = [] }: CaptureConfig) => getPropertiesFromElement(element, pseudo, variables)

const getCaptures = (captures = []) => (
  captures.reduce((acc, { element, pseudo, variables = [] }) => ({ 
    ...acc, 
    ...getCapture({element, pseudo, variables}) 
  }), {})
);

const getBootstrapBreakpoints = () => {
  const configForBootstrap: CaptureConfig = {
    element: document.body,
    variables: [
      {
        key: 'xs',
        name: '--breakpoint-xs',
        fallback: 576,
      },
      {
        key: 'sm',
        name: '--breakpoint-sm',
        fallback: 768,
      },
      {
        key: 'md',
        name: '--breakpoint-md',
        fallback: 992,
      },
      {
        key: 'lg',
        name: '--breakpoint-lg',
        fallback: 1200,
      },
      {
        key: 'xl',
        name: null,
        fallback: 10000, // NOTE: intentionally huge, can't use Infinity in media queries
      },
    ]
  };

  return getCapture(configForBootstrap);
}

/* EXPORTS */
export const getBreakpoints = ({ custom = {}, captures = [] } = {}): MqConfig => {
  const captured = getCaptures(captures);

  return {
    ...bootstrapDefaultBreakpoints,
    ...custom,
    ...captured
  }
}

export const bootstrapDefaultBreakpoints = {
  ...getBootstrapBreakpoints(),
};

export const convertMQsForVueComponentMediaQueriesLibrary = (mqConfig: MqConfig) => {
	return Object.entries(mqConfig).reduce((acc, [key, value]) => {
		return {
			...acc,
      // parseInt will cause Infinity to be NaN
			[key]: `(max-width: ${ !Number.isNaN(Number.parseInt(`${ value }`)) ? `${ value }px` : value })`
		}
	},  {});
}

export const regexMQ = /(\d+)/gi

export const getSortedMediaQueriesAsTuples = (queries: Record<string, string>, order: SortOrder = 'asc') => {
  const bps = [...Object.entries(queries)].map(([ key, mq ]) => {
    const match = mq.match(regexMQ)
    return [key, match?.[0]]
  });
  if (order === 'asc') return bps.sort((a, b) => (a[1]! < b[1]!) ? 0 : -1);
  if (order === 'desc') return bps.sort((a, b) => (a[1]! < b[1]!) ? -1 : 0);
}